import React, { useEffect, useState } from "react";
import PageLink from "../PageLink/PageLink";
import { career } from "../../../config/career";
import "./JobBanner.scss";

// Schmales Hinweis-Banner ganz oben auf allen Seiten. Klick fuehrt zur
// Karriere-Seite, das X blendet es aus. Inhalt & Aktivierung werden
// ausschliesslich ueber src/config/career.ts gepflegt.
const STORAGE_PREFIX = "hp-jobbanner-dismissed-";
// Muss zur Hoehe in JobBanner.scss passen – die Klasse schiebt die Navbar
// unter das Banner, damit Logo und Menue klickbar bleiben.
const BODY_CLASS = "hp-jobbanner-visible";

// Layout und damit das Banner werden bei jedem Seitenwechsel neu gemountet.
// Ohne dieses modulweite Merkmal liefe die Einblend-Animation jedes Mal erneut.
let hasAnimated = false;

// Waehrend eines Seitenwechsels haelt gatsby-plugin-transition-link die alte
// Seite noch im Baum, waehrend die neue bereits montiert ist - es gibt also
// kurzzeitig zwei Banner. Ein einfaches "beim Aufraeumen Klasse entfernen"
// loeschte deshalb die Klasse, die die neue Instanz gerade gesetzt hatte, und
// die Navigationsleiste rutschte unter das Banner. Daher gezaehlt statt
// geschaltet: die Klasse faellt erst, wenn keine sichtbare Instanz mehr da ist.
let visibleInstances = 0;

// Zaehlen allein reicht nicht ganz: Die alte Seite verschwindet am Ende der
// exit-Phase, die neue erscheint erst nach der entry-Verzoegerung - beides
// bei 0,5 s, aber nicht im selben Frame. In der Luecke steht der Zaehler auf
// 0, die Klasse fiele, und die neue Navbar wuerde bei top 0 montiert, um dann
// eine halbe Sekunde lang unter dem Banner hervorzurutschen. Solange waere
// die obere Menuezeile verdeckt. Darum wird das Entfernen kurz verzoegert und
// abgebrochen, sobald sich die naechste Instanz meldet. Nur das bewusste
// Wegklicken entfernt die Klasse sofort.
const REMOVE_DELAY_MS = 300;
let removeTimer: ReturnType<typeof setTimeout> | null = null;
let removeImmediately = false;

function syncBodyClass() {
  if (typeof document === "undefined") return;
  if (removeTimer) {
    clearTimeout(removeTimer);
    removeTimer = null;
  }
  if (visibleInstances > 0) {
    document.body.classList.add(BODY_CLASS);
    return;
  }
  if (removeImmediately) {
    removeImmediately = false;
    document.body.classList.remove(BODY_CLASS);
    return;
  }
  removeTimer = setTimeout(() => {
    removeTimer = null;
    if (visibleInstances === 0) document.body.classList.remove(BODY_CLASS);
  }, REMOVE_DELAY_MS);
}

const JobBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [animate, setAnimate] = useState(false);
  const { enabled, id, label, role, cta } = career.banner;
  const storageKey = `${STORAGE_PREFIX}${id}`;

  useEffect(() => {
    if (!enabled) return;
    // localStorage ist in privaten Fenstern oder bei blockierten Site-Daten
    // nicht immer lesbar – dann wird das Banner einfach jedes Mal gezeigt.
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(storageKey) === "1";
    } catch (e) {
      dismissed = false;
    }
    setVisible(!dismissed);
    if (!dismissed && !hasAnimated) {
      hasAnimated = true;
      setAnimate(true);
    }
  }, [enabled, storageKey]);

  useEffect(() => {
    if (!visible) return;
    visibleInstances += 1;
    syncBodyClass();
    return () => {
      visibleInstances -= 1;
      syncBodyClass();
    };
  }, [visible]);

  function dismiss() {
    removeImmediately = true;
    setVisible(false);
    try {
      localStorage.setItem(storageKey, "1");
    } catch (e) {
      // Ohne Speicher erscheint das Banner beim naechsten Aufruf erneut.
    }
  }

  if (!enabled || !visible) return null;

  return (
    <aside
      className={`JobBanner${animate ? " JobBanner--enter" : ""}`}
      aria-label="Stellenangebot"
    >
      <PageLink
        className="JobBanner__link"
        to="/karriere"
      >
        <span className="JobBanner__label">{label}</span>
        {role && <span className="JobBanner__role">{role}</span>}
        <span className="JobBanner__cta">
          <span className="JobBanner__ctaText">{cta}</span>
          {/* Pfeil per CSS gezeichnet – ein "→" haengt sonst davon ab, dass
              der jeweilige Font das Zeichen ueberhaupt kennt. */}
          <span className="JobBanner__arrow" aria-hidden="true" />
        </span>
      </PageLink>
      <button
        type="button"
        className="JobBanner__close"
        onClick={dismiss}
        aria-label="Hinweis schließen"
      >
        ✕
      </button>
    </aside>
  );
};

export default JobBanner;
